import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { provideRouter, withComponentInputBinding, withHashLocation } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HashLocationStrategy } from '@angular/common';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

import { provideToastr } from 'ngx-toastr';

import { routes } from './app.routes';
import { jwtDecode } from 'jwt-decode';
import { environment } from '@env/environment';
import { KeycloakBearerInterceptor, KeycloakService } from 'keycloak-angular';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { tokenInterceptor } from '@core/interceptors/token.interceptor';
import { sanitizeInterceptor } from '@core/interceptors/sanitize.interceptor';
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha';

function deleteCookie(name: string) {
  document.cookie = name + '=; Max-Age=-99999999;';
  document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
  document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + location.hostname;
}

const currentTime = Math.floor(Date.now() / 1000);

function initializeKeycloak(keycloak: KeycloakService) {
  return () =>
    keycloak
      .init({
        config: {
          url: `${environment.keycloakBaseUrl}/auth`,
          realm: `${environment.keycloakRealm}`,
          clientId: `${environment.keycloakClientId}`,
        },

        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html',
        },
        bearerExcludedUrls: ['/assets/', '/*'],
      })
      .then(async (authenticated) => {
        if (authenticated) {
          try {
            const [accessToken, userProfile] = await Promise.all([keycloak.getToken(), keycloak.loadUserProfile()]);
            const data = {
              accessToken,
              idToken: keycloak.getKeycloakInstance().idToken,
              refreshToken: keycloak.getKeycloakInstance().refreshToken,
              userProfile,
              username: keycloak.getUsername(),
              roles: keycloak.getUserRoles(),
              tokenParsed: keycloak.getKeycloakInstance().tokenParsed,
              profile: keycloak.getKeycloakInstance().profile,
              idTokenParsed: keycloak.getKeycloakInstance().idTokenParsed,
            };

            interface DecodedToken {
              preferred_username: string;
              given_name: string;
              family_name: string;
              email: string;
              sub: string;
              exp: number;
            }

            const decodedToken: DecodedToken = jwtDecode(data.accessToken) as DecodedToken;

            const cuitCuilt = decodedToken.preferred_username;

            const user = {
              cuitCuilt: cuitCuilt,
              nombre: decodedToken.given_name,
              apellido: decodedToken.family_name,
              email: decodedToken.email,
              roles: data.roles,
              id: decodedToken.sub,
            };
            const roles = data.roles;

            if (currentTime < decodedToken.exp) {
              localStorage.setItem('username', JSON.stringify({ status: true, accessToken: data.accessToken, user, roles }));
            } else {
              window.location.href = environment.logout;

              window.localStorage.clear();

              document.cookie.split(';').forEach(function (cookie) {
                const eqPos = cookie.indexOf('=');
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
                document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
              });

              document.cookie.split(';').forEach(function (cookie_1) {
                const eqPos_1 = cookie_1.indexOf('=');
                const name_1 = eqPos_1 > -1 ? cookie_1.substr(0, eqPos_1).trim() : cookie_1.trim();
                document.cookie = name_1 + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=' + location.hostname;
              });

              deleteCookie('AUTH_SESSION_ID_LEGACY');
              deleteCookie('KEYCLOAK_IDENTITY_LEGACY');
              deleteCookie('_KC_RESTART');
            }
            return data;
          } catch (error) {
            console.error('Error al obtener datos de Keycloak', error);
            return null;
          }
        } else {
          throw new Error('Keycloak authentication failed');
        }
      })
      .catch(() => {
        return null;
      });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding(), withHashLocation()),
    provideAnimations(),
    HashLocationStrategy,
    importProvidersFrom(RecaptchaV3Module),
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: '' },
    // Toastr
    provideToastr({
      positionClass: 'toast-top-center',
      preventDuplicates: false,
      progressBar: true,
      progressAnimation: 'increasing',
      timeOut: 4000,
      easeTime: 300,
      closeButton: true,
      tapToDismiss: true,
      extendedTimeOut: 1000,
    }),

    provideHttpClient(withInterceptors([tokenInterceptor, sanitizeInterceptor])),

    provideAnimationsAsync(),

    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KeycloakBearerInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'es' },
    KeycloakService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
};
